/**
 * The lab upload, shared by every place on the page that offers it: the header button, the hero
 * button and the sticky bar on phones all open the same file picker and fill one list of files.
 * On the pages that render `WindowDropTarget` (the landing page, /start and /account/labs) the whole
 * window is also a drop target, since a visitor from an ad may have the PDF on their desktop and
 * will drop it wherever their pointer is. Everywhere else a dropped file is refused, so a clinician
 * on a patient page or a customer at checkout is neither sent into onboarding nor has the browser
 * replace the page with the file.
 *
 * The real `<input type="file">` is never visible or in the tab order. Each button is the control,
 * so keyboard and screen reader users meet one thing, not two.
 *
 * Files chosen here are held in memory, then /start uploads them one by one.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode, RefObject } from "react";
import { createContext, use, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/shadow.stylex";
import { shadow } from "@lattice/tokens/shadow.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { dark } from "@lattice/tokens/themes";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import { Button } from "@lattice/ui/Button";
import { LAB_FILE_ACCEPT } from "@lattice/core";
import { instant, useJumpUntilReported } from "./instant.ts";
import { navigate } from "./router.ts";
const styles = {
  overlay: {
    kVAEAm: "xixxii4",
    kpwlN0: "x1d1h8al",
    kY2c9j: "x1355qak",
    k1xSpc: "xrvj5dj",
    kgQiWS: "x1ku5rj1",
    kaIpWk: "x1hegsvj",
    kWkggS: "x1l0w1cl",
    kMwMTN: "xzb3lse",
    kGuDYH: "x154s7bo",
    k63SB2: "xqu410z",
    kQqvRs: "x1ntj4s9",
    kb6lSQ: "x1d3j7mx",
    kfzvcC: "x47corl",
    kSiTet: "xg01cxk",
    k1ekBW: "x19991ni",
    kIyJzY: "x149xavy",
    $$css: true
  },
  overlayShown: {
    kSiTet: "x179ler2",
    $$css: true
  },
  bar: {
    k1xSpc: "x1s85apg xqupn85 xod88zy",
    kVAEAm: "xixxii4",
    kkqhue: "x1i2vcpo",
    krVfgx: "xhdlry7",
    kY2c9j: "x1n327nk",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x626il7",
    k8WAf4: "x1t0vds8",
    kZCmMZ: "xa9f7qn",
    kwRFfy: "x1icxu4v",
    kaIpWk: "x1m3fsnl",
    kWkggS: "x1l0w1cl",
    kMwMTN: "xzb3lse",
    kGuDYH: "xl4dl4e",
    kGVxlE: "x8xb0n0",
    k1ekBW: "xwcsmn1",
    kIyJzY: "x937rjr",
    kAMwcw: "xa02eh3",
    $$css: true
  },
  barHidden: {
    k3aq6I: "x1loycy4 xuupxpy",
    kSiTet: "xg01cxk",
    kfzvcC: "x47corl",
    $$css: true
  }
};

/** A file the visitor chose, with an id that stays the same while it uploads. */
export interface Picked {
  readonly id: string;
  readonly file: File;
}
interface Uploads {
  /** Every file chosen this visit and not removed, oldest first. */
  readonly picked: readonly Picked[];
  readonly remove: (id: string) => void;
  readonly choose: () => void;
  /** Opens the phone's camera instead of its file browser. */
  readonly takePhoto: () => void;
}
const UploadContext = createContext<Uploads>({
  picked: [],
  remove: () => undefined,
  choose: () => undefined,
  takePhoto: () => undefined
});
export function useUploads(): Uploads {
  return use(UploadContext);
}
const DropContext = createContext<(accepting: boolean) => void>(() => undefined);

/**
 * Rendered by a page where a file dropped anywhere on the window is the customer's own lab report.
 * While one is mounted the window takes dropped files, and on every other page it refuses them.
 */
export function WindowDropTarget(): null {
  const setAccepting = use(DropContext);
  useEffect(() => {
    setAccepting(true);
    return () => setAccepting(false);
  }, [setAccepting]);
  return null;
}

/** Whether a drag carries files, as opposed to selected text or a link being dragged. */
function carriesFiles(event: DragEvent): boolean {
  return event.dataTransfer?.types.includes("Files") ?? false;
}

/**
 * Without a listener the browser opens a dropped file in place of the page, so a page that takes no
 * files still cancels the drop. dropEffect "none" shows the not-allowed cursor during the drag.
 */
function refuseDrops(): () => void {
  const refuse = (event: DragEvent) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
  };
  window.addEventListener("dragover", refuse);
  window.addEventListener("drop", refuse);
  return () => {
    window.removeEventListener("dragover", refuse);
    window.removeEventListener("drop", refuse);
  };
}
function acceptDrops(onFiles: RefObject<(files: FileList) => void>, setDragging: (dragging: boolean) => void): () => void {
  /*
    dragenter and dragleave fire for every child element crossed, so counting them is the only
    way to know the drag has really left the window. A boolean flickers off over each child.
  */
  let depth = 0;
  const enter = (event: DragEvent) => {
    if (!carriesFiles(event)) return;
    depth += 1;
    setDragging(true);
  };
  const leave = () => {
    depth = Math.max(0, depth - 1);
    if (depth === 0) setDragging(false);
  };

  /*
    dragover must call preventDefault or the browser will not fire drop. Finder drags on Safari
    often omit "Files" from types until the drop itself, so we cannot gate preventDefault on that.
  */
  const over = (event: DragEvent) => {
    event.preventDefault();
  };
  const drop = (event: DragEvent) => {
    event.preventDefault();
    depth = 0;
    setDragging(false);
    const dropped = event.dataTransfer?.files;
    if (dropped && dropped.length > 0) onFiles.current(dropped);
  };
  window.addEventListener("dragenter", enter);
  window.addEventListener("dragleave", leave);
  window.addEventListener("dragover", over);
  window.addEventListener("drop", drop);
  return () => {
    window.removeEventListener("dragenter", enter);
    window.removeEventListener("dragleave", leave);
    window.removeEventListener("dragover", over);
    window.removeEventListener("drop", drop);
    setDragging(false);
  };
}

/** Window-level drag and drop. Returns whether a file drag is over a window that takes files. */
function useWindowDrop(onFiles: (files: FileList) => void, accepting: boolean): boolean {
  const [dragging, setDragging] = useState(false);
  /* The latest callback, so the window listeners are not re-added on every render. */
  const latest = useRef(onFiles);
  latest.current = onFiles;
  useEffect(() => accepting ? acceptDrops(latest, setDragging) : refuseDrops(), [accepting]);
  return dragging;
}

/** The file dialog opens on the upload step, not on whatever page the button sat on. */
function openOnStart(input: RefObject<HTMLInputElement | null>): void {
  if (!window.location.pathname.startsWith("/start")) {
    flushSync(() => {
      navigate("/start");
    });
  }
  input.current?.click();
}
function toPicked(files: FileList | readonly File[]): Picked[] {
  return Array.from(files).map(file => ({
    id: crypto.randomUUID(),
    file
  }));
}

/**
 * Holds the chosen files for the whole visit and owns the hidden inputs and the window drop
 * target, which is live only while a page renders `WindowDropTarget`. `onAdded` runs whenever
 * files arrive, which is how the landing page moves into the flow.
 */
export function UploadProvider({
  children,
  onAdded
}: {
  readonly children: ReactNode;
  readonly onAdded: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<readonly Picked[]>([]);
  const arriving = useRef(false);
  const add = (files: FileList | readonly File[] | null) => {
    if (!files || files.length === 0) return;
    /*
      Read the files now, not inside the state updater. A FileList is live, so clearing the input
      right after this call empties it, and React runs the updater later whenever the provider
      already has a render pending, which would add nothing.
    */
    const added = toPicked(files);
    arriving.current = true;
    setPicked(before => [...before, ...added]);
  };
  useEffect(() => {
    if (!arriving.current) return;
    arriving.current = false;
    onAdded();
  }, [picked, onAdded]);
  const [accepting, setAccepting] = useState(false);
  const dragging = useWindowDrop(add, accepting);
  return <DropContext value={setAccepting}>
      <UploadContext value={{
      picked,
      remove: id => setPicked(before => before.filter(entry => entry.id !== id)),
      choose: () => openOnStart(input),
      takePhoto: () => camera.current?.click()
    }}>
        {children}
        <input ref={input} type="file" multiple accept={LAB_FILE_ACCEPT} tabIndex={-1} aria-hidden="true" data-testid="lab-files" onChange={event => {
        add(event.currentTarget.files);
        event.currentTarget.value = "";
      }} className="x1s85apg" />
        <input ref={camera} type="file" accept="image/*" capture="environment" tabIndex={-1} aria-hidden="true" onChange={event => {
        add(event.currentTarget.files);
        event.currentTarget.value = "";
      }} className="x1s85apg" />
        <div {...stylex.props(dark, styles.overlay, dragging && styles.overlayShown)} aria-hidden="true">
          Drop your labs
        </div>
      </UploadContext>
    </DropContext>;
}

/** A button anywhere on the page that opens the shared file picker. */
export function UploadButton({
  label,
  size = "regular",
  tone = "solid"
}: {
  readonly label: string;
  readonly size?: "regular" | "small";
  readonly tone?: "solid" | "quiet";
}) {
  const {
    choose
  } = use(UploadContext);
  return <Button size={size} tone={tone} onClick={choose}>
      {label}
    </Button>;
}

/** The hero's upload: the main button and what it accepts. Choosing files moves into the flow. */
export function Upload() {
  const {
    choose
  } = use(UploadContext);
  return <div className="x78zum5 xdt5ytf x626il7 x1cy8zhl" id="upload">
      <div className="x78zum5 x1a02dak x6s0dn4 x1vdzsm x1xwure6">
        <Button testId="upload-labs" onClick={choose}>
          Upload your labs
        </Button>
        <span className="xl4dl4e x1jty25b">
          PDF, photo or screenshot. No card needed.
          <span className="xt0psk2 x1jre7q"> Or drop them anywhere.</span>
        </span>
      </div>
      <span className="xl4dl4e x1jty25b">We do not sell your labs.</span>
    </div>;
}

/**
 * The phone-only bar that keeps the upload in reach once the hero button scrolls away. It hides
 * while the hero button is on screen so the two are never visible together.
 */
export function StickyUpload() {
  const {
    choose
  } = use(UploadContext);
  const {
    jumping,
    report
  } = useJumpUntilReported();
  /* Hidden while the hero's own button, the price card or the footer is on screen, so it never covers them. */
  const [covering, setCovering] = useState<ReadonlySet<Element>>(new Set([]));
  const [watching, setWatching] = useState(false);
  useEffect(() => {
    const targets = [document.getElementById("upload"), document.querySelector("#pricing section"), document.querySelector("footer")].filter(target => target !== null);
    const observer = new IntersectionObserver(entries => {
      setWatching(true);
      report();
      setCovering(now => {
        const next = new Set(now);
        for (const entry of entries) if (entry.isIntersecting) next.add(entry.target);else next.delete(entry.target);
        return next;
      });
    });
    for (const target of targets) observer.observe(target);
    return () => observer.disconnect();
  }, [report]);

  /* Hidden until the first look, so it does not flash over the hero before the observers report. */
  const hidden = !watching || covering.size > 0;
  return <div {...stylex.props(dark, styles.bar, hidden && styles.barHidden, jumping && instant)} aria-hidden={hidden}
  /* Out of view it is also out of the tab order, or focus would land on a button nobody sees. */ inert={hidden} data-testid="sticky-upload">
      <span>$175 a month</span>
      <Button size="small" onClick={choose}>
        Upload your labs
      </Button>
    </div>;
}